import { useContext, useMemo } from 'react'
import LiveReportRenderContext from '../StateManagment/LiveReportRenderContext'
import { ReportBrandingContext } from '../components/liveReport/reportBuilder2/context/ReportBrandingContext'
import useSettings from './Settings'
import { resolveChartColors } from '../helpers/visualisationDefaults'

/**
 * Waar een grafiek zijn kleuren vandaan haalt.
 *
 * Dashboard en Analyse krijgen het vaste Deccos-palet; een live rapportage
 * houdt de huisstijl van de organisatie en de kleuren die de rapportagemaker
 * koos. De grafiekcomponenten zijn gedeeld — `EffectVisualization` rendert op
 * een dashboardkaart én in een LiveReport2-widget — dus die keuze hangt aan
 * `LiveReportRenderContext` en niet aan het bestand.
 *
 * Gebruik altijd béide teruggaven. `overrides` los laten hangen en alleen
 * `palette` overnemen geeft het ene beeld dat we niet willen: vaste kleuren met
 * daarbovenop nog iemands oude kaartkeuzes.
 *
 * WAAROM DE BRANDING-CONTEXT VÓÓR `useSettings()` GAAT. `useSettings` leest
 * Firestore als de ingelogde gebruiker. Op de gedeelde rapportage is niemand
 * ingelogd: die lees levert niets op, de hook houdt zijn beginwaarden, en dan
 * tekent élke grafiek in het generieke roze/cyaan in plaats van de huisstijl
 * van de eigenaar -- zonder fout en zonder waarschuwing. `getPublicLiveReport2`
 * lost de kleuren daarom server-side op en `LiveReportOpen2` zet ze in de
 * branding-context, dezelfde weg die het logo al liep. Binnen de app staat er
 * geen provider, dus valt het vanzelf terug op de ingelogde organisatie.
 * AI-CONTEXT: See docs/pitfalls.md#state-firestore-lookup-empty-in-template-mode
 *
 * AI-CONTEXT: See docs/features/chart-color-system.md
 *
 * @param {object} [args]
 * @param {Array<string>} [args.colorPalette] - Palet dat een live rapportage meegeeft
 * @param {Object<string, string>} [args.colorOverrides] - Per-item gekozen kleuren
 * @returns {{palette: Array<string>, overrides: Object<string, string>|undefined, inLiveReport: boolean}}
 */
const useChartColors = ({ colorPalette, colorOverrides } = {}) => {
    const inLiveReport = useContext(LiveReportRenderContext)
    const branding = useContext(ReportBrandingContext)
    const { PrimaryColor, SecundairyColor, TertairyColor } = useSettings()

    // Per kleur terugvallen, niet per set: mist de eigenaar één huisstijlkleur,
    // dan hoort daar de standaard te staan en niet een gat.
    const primary = branding?.primaryColor || PrimaryColor
    const secundairy = branding?.secundairyColor || SecundairyColor
    const tertairy = branding?.tertairyColor || TertairyColor

    return useMemo(() => {
        const { palette, overrides } = resolveChartColors({
            inLiveReport,
            colorPalette,
            colorOverrides,
            settings: {
                PrimaryColor: primary,
                SecundairyColor: secundairy,
                TertairyColor: tertairy,
            },
        })
        return { palette, overrides, inLiveReport }
    }, [inLiveReport, colorPalette, colorOverrides, primary, secundairy, tertairy])
}

export default useChartColors
